import React from "react";
import SuggestionSkeleton from "./SuggestionSkeleton";
import "./SearchStates.css";

/**
 * Distinct, explicit states for a suggestion search. Each is a separate
 * component instead of a single generic "something went wrong" so callers can
 * give every case its own copy (i18n via the `message` props they pass in).
 */

/** Idle: no query yet / query too short. */
export function EmptyState({ message = "Start typing to search", className = "" }) {
  return (
    <div className={`search-state search-state--idle ${className}`.trim()}>
      {message}
    </div>
  );
}

/** Success but the backend returned zero matches. */
export function NoResultsState({ message = "No matches found", className = "" }) {
  return (
    <div className={`search-state search-state--empty ${className}`.trim()} role="status">
      {message}
    </div>
  );
}

/** Network / backend failure, with an explicit (non-auto) retry affordance. */
export function ErrorState({
  message = "Couldn't load results",
  retryLabel = "Retry",
  onRetry,
  className = "",
}) {
  return (
    <div className={`search-state search-state--error ${className}`.trim()} role="alert">
      <span className="search-state__msg">{message}</span>
      {onRetry && (
        <button type="button" className="search-state__retry" onClick={onRetry}>
          {retryLabel}
        </button>
      )}
    </div>
  );
}

/**
 * Convenience renderer that maps a `useSuggestionSearch` result to the right UI:
 * skeleton while loading, ErrorState (with retry) on failure, NoResultsState on
 * empty, EmptyState when idle (only if `texts.idle` is provided). Returns null
 * when there are results (the caller renders the real list itself).
 *
 * @param {Object}   props
 * @param {Object}   props.state         the object returned by useSuggestionSearch
 * @param {number}   [props.skeletonRows] rows for the loading skeleton
 * @param {Function} [props.onRetry]      retry handler (usually state.retry)
 * @param {Object}   [props.texts]        { idle, noResults, error, retry } strings
 */
export function SuggestionStatus({ state, skeletonRows = 5, onRetry, texts = {} }) {
  if (!state) return null;
  if (state.isLoading) return <SuggestionSkeleton rows={skeletonRows} />;
  if (state.isError) {
    return (
      <ErrorState
        message={texts.error}
        retryLabel={texts.retry}
        onRetry={onRetry || state.retry}
      />
    );
  }
  if (state.isEmpty) return <NoResultsState message={texts.noResults} />;
  if (state.isIdle && texts.idle) return <EmptyState message={texts.idle} />;
  return null;
}
